import { useArAsset } from './useArAsset';

interface Props {
  menuItemId: string;
  itemNameAr: string;
}

/**
 * AR Quick Look launcher.
 *
 * On iOS / iPadOS, an `<a rel="ar" href="...usdz">` tag is auto-detected
 * by Safari/WebKit and opens the system AR Quick Look viewer (ARKit)
 * full-screen. No JS, no extra SDK.
 *
 * On non-iOS clients the link still works as a download link, which is
 * acceptable: the page degrades to "you can preview the model in any
 * USDZ-aware viewer". For the iPad menu-tablet (the primary target)
 * this is the canonical AR entry point.
 */
export function ArQuickLook({ menuItemId, itemNameAr }: Props): JSX.Element {
  const { asset, isLoading, error, request } = useArAsset(menuItemId);

  if (isLoading) {
    return (
      <div className="rounded-aurum-md border border-aurum-500/15 bg-onyx-900/40 px-4 py-3 font-mono text-xs text-onyx-100">
        جاري التحقق من نموذج الـ AR...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-aurum-md border border-accent-ruby/30 bg-accent-ruby/10 px-4 py-3 text-xs text-accent-ruby">
        {error}
      </div>
    );
  }

  if (asset && (asset.status === 'ready' || asset.status === 'cached') && asset.usdzUrl) {
    return (
      <a
        rel="ar"
        href={asset.usdzUrl}
        // The single inner img child is required by Apple Quick Look —
        // it picks it up as the poster while loading the USDZ.
        className="group block rounded-aurum-md border border-aurum-500/30 bg-aurum-500/10 px-4 py-3 text-center transition hover:bg-aurum-500/20"
        aria-label={'فتح ' + itemNameAr + ' بالواقع المعزز'}
      >
        <img
          src="data:image/svg+xml;utf8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20width%3D'1'%20height%3D'1'%2F%3E"
          alt=""
          className="hidden"
        />
        <span className="block font-display text-base text-aurum-200">
          ✦ معاينة بالواقع المعزز
        </span>
        <span className="mt-1 block font-mono text-[10px] uppercase tracking-widest text-aurum-300/70">
          AR Quick Look · iPadOS
        </span>
      </a>
    );
  }

  // pending / generating / failed → offer to register a generation request.
  return (
    <button
      onClick={() => void request()}
      className="block w-full rounded-aurum-md border border-dashed border-aurum-500/30 bg-onyx-900/40 px-4 py-3 text-center text-xs text-cream-100/70 hover:border-aurum-500/50 hover:text-aurum-200"
    >
      نموذج الـ AR قيد الإعداد — اطلب الإشعار عند الجاهزية
    </button>
  );
}
